/**
 * Vertical resizable split for strip mode — SuggestionStrip on top,
 * report editor on bottom, with a 4px drag handle between them. Default
 * 50/50; the rad's preferred ratio persists across reloads via
 * `stripHeightFractionAtom` (localStorage).
 *
 * Pointer capture on the handle lets the drag follow the cursor even if
 * it leaves the handle (same pattern as the right-sidebar resize in
 * CaseViewer).
 */

import { useCallback, useEffect, useRef } from "react";
import { useAtom } from "jotai";

import {
  STRIP_HEIGHT_MAX,
  STRIP_HEIGHT_MIN,
  stripHeightFractionAtom,
} from "../../atoms/reporterPrefsAtoms";

interface StripSplitProps {
  top: React.ReactNode;
  bottom: React.ReactNode;
}

export function StripSplit({ top, bottom }: StripSplitProps) {
  const [fraction, setFraction] = useAtom(stripHeightFractionAtom);
  // Holds the active drag's listener pair so unmount mid-drag can detach.
  const activeDragRef = useRef<{ onMove: (e: PointerEvent) => void; onUp: () => void } | null>(
    null,
  );

  // If the component unmounts mid-drag (mode flip back to inline, route
  // change), detach any in-flight document listeners so they don't leak.
  useEffect(() => {
    return () => {
      const active = activeDragRef.current;
      if (active) {
        document.removeEventListener("pointermove", active.onMove);
        document.removeEventListener("pointerup", active.onUp);
        activeDragRef.current = null;
      }
    };
  }, []);

  const onPointerDown = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      e.preventDefault();
      const handle = e.currentTarget;
      const parent = handle.parentElement;
      if (!parent) return;
      const parentRect = parent.getBoundingClientRect();

      const onMove = (moveEvent: PointerEvent) => {
        const offsetY = moveEvent.clientY - parentRect.top;
        const next = offsetY / parentRect.height;
        const clamped = Math.max(STRIP_HEIGHT_MIN, Math.min(STRIP_HEIGHT_MAX, next));
        setFraction(clamped);
      };
      const onUp = () => {
        document.removeEventListener("pointermove", onMove);
        document.removeEventListener("pointerup", onUp);
        activeDragRef.current = null;
      };
      document.addEventListener("pointermove", onMove);
      document.addEventListener("pointerup", onUp);
      activeDragRef.current = { onMove, onUp };
    },
    [setFraction],
  );

  const safe = Math.max(STRIP_HEIGHT_MIN, Math.min(STRIP_HEIGHT_MAX, fraction));

  return (
    <div className="strip-split">
      <div className="strip-split__top" style={{ flexBasis: `${safe * 100}%` }}>
        {top}
      </div>
      <div
        role="separator"
        aria-orientation="horizontal"
        aria-label="Resize AI suggestions vs report"
        className="strip-split__handle"
        onPointerDown={onPointerDown}
        title="Drag to resize"
      />
      <div className="strip-split__bottom">{bottom}</div>
    </div>
  );
}
